import { getOrgId, getToken, getUserInfo } from '../../../../shared/core/utils/auth-utils';
import { ConsultationContactsResponse, ConsultationHistoryResponse, ConsultNotesResponse, ConsultNote, OrdersResponse, OrderItemsResponse, TargetType, ConsultationHistoryItem } from '../types/consultation';
import Profile from '../../../../shared/core/models/profile';

/**
 * Construct the authenticated image URL
 */
export const getPrescriptionImageUrl = (imageKey: string): string => {
  const token = getToken();
  return `/api/auth/v1/docs/get?key=${encodeURIComponent(imageKey)}&token=${token}`;
};

/**
 * Logic to parse and extract image keys from consultation data
 */
export const extractPrescriptionImages = (item: ConsultationHistoryItem) => {
  try {
    const consultImages = JSON.parse(item.consultationDesc?.consultImages || "[]");
    const diagnoseImages = JSON.parse(item.diagnosis?.diagnoseImages || "[]");
    
    // Also handle scribble images if inputType is 'IMAGE'
    const consultScribbles = item.consultationDesc?.inputType === 'IMAGE' 
      ? JSON.parse(item.consultationDesc?.value || "[]") : [];
      
    const diagnoseScribbles = item.diagnosis?.inputType === 'IMAGE'
      ? JSON.parse(item.diagnosis?.value || "[]") : [];

    return {
      photos: [...(Array.isArray(consultImages) ? consultImages : []), ...(Array.isArray(diagnoseImages) ? diagnoseImages : [])].filter(Boolean),
      scribbles: [...(Array.isArray(consultScribbles) ? consultScribbles : []), ...(Array.isArray(diagnoseScribbles) ? diagnoseScribbles : [])].filter(Boolean)
    };
  } catch (e) {
    console.error('Error parsing prescription images:', e);
    return { photos: [], scribbles: [] };
  }
};

class ConsultationService {
  /**
   * Helper to get orgType from token/userInfo
   */
  private getOrgType(): string {
    const userInfo = getUserInfo() as any;
    const orgId = getOrgId();
    const orgEntry = userInfo?.args?.orgs?.[orgId];
    return orgEntry?.orgType || userInfo?.type || '';
  }

  /**
   * Helper to get common headers
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Authorization': getToken() || '',
      'orgId': getOrgId() || '',
      'Content-Type': 'application/json',
    };
    
    const orgType = this.getOrgType();
    if (orgType) {
      headers['orgType'] = orgType;
    }
    
    return headers;
  }

  /**
   * Fetch consultation contacts from API
   */
  async getConsultationContacts(pageSize: number = 20, nextLink?: string): Promise<ConsultationContactsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('pageSize', String(pageSize));
      if (nextLink) {
        queryParams.append('nextLink', nextLink);
        queryParams.append('nextState', nextLink);
      }

      const response = await fetch(`/api/consult/contacts/v1/get?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as ConsultationContactsResponse;
    } catch (error) {
      console.error('Error fetching consultation contacts:', error);
      throw error;
    }
  }

  /**
   * Upsert consultation contacts
   */
  async upsertConsultationContacts(payload: {
    consultationContacts: Array<{
      type: string;
      name: string;
      description?: string;
      attr?: Record<string, string>;
      targetId: string;
      targetType: TargetType;
    }>;
  }): Promise<any> {
    try {
      const response = await fetch('/api/consult/contacts/v1/upsert', {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to upsert contacts: ${response.status} ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error upserting consultation contacts:', error);
      throw error;
    }
  }

  /**
   * Fetch user profile by targetId and targetType
   */
  async getUserProfile(targetId: string, targetType: string): Promise<Profile> {
    try {
      const response = await fetch(`/api/auth/v1/profile/${targetId}/${targetType}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.body as Profile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  /**
   * Fetch consultation history for a user
   */
  async getConsultationHistory(customerId: string, pageSize: number = 10): Promise<ConsultationHistoryResponse> {
    try {
      const response = await fetch(`/api/consultation/v1/get?customerId=${customerId}&pageSize=${pageSize}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch consultation history: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as ConsultationHistoryResponse;
    } catch (error) {
      console.error('Error fetching consultation history:', error);
      throw error;
    }
  }

  /**
   * Save/Update consultation
   */
  async saveConsultation(payload: {
    consultation: {
      consultationId?: string;
      vendorId?: string;
      customerId: string;
      customerName: string;
      consultationDesc: {
        symptoms?: string;
        inputType?: string;
        consultImages?: string;
        value?: string;
      };
      diagnosis: {
        tests?: string;
        inputType?: string;
        diagnoseImages?: string;
        value?: string;
      };
      prescribe?: {
        tabs?: string;
      };
      timestamp?: string;
    };
    selectedVendors: Array<{
      userId: string;
      userType: 'LAB' | 'PHARMACY';
    }>;
  }): Promise<any> {
    try {
      const response = await fetch('/api/consultation/v1/upsert', {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save consultation: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving consultation:', error);
      throw error;
    }
  }

  /**
   * Alias for saveConsultation to match mobile naming
   */
  async savePrescription(payload: any) {
    return this.saveConsultation(payload);
  }

  /**
   * Alias for deleteConsultation to match mobile naming
   */
  async deletePrescription(consultationId: string, vendorId: string, customerId: string, timestamp: string) {
    return this.deleteConsultation({
      consultation: { consultationId, vendorId, customerId, timestamp }
    });
  }

  /**
   * Fetch consultation notes for a user
   */
  async getConsultNotes(customerId: string): Promise<ConsultNotesResponse> {
    try {
      const response = await fetch(`/api/consultation/v1/notes/${customerId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as ConsultNotesResponse;
    } catch (error) {
      console.error('Error fetching consultation notes:', error);
      throw error;
    }
  }

  /**
   * Save/Update consultation note
   */
  async saveConsultNote(payload: {
    consultNote: {
      customerId: string;
      customerType: string;
      customerName: string;
      value: string;
      valueHtml?: string;
      type: string;
    };
  }): Promise<any> {
    try {
      const response = await fetch('/api/consultation/v1/notes/upsert', {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save note: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving consultation note:', error);
      throw error;
    }
  }

  /**
   * Delete consultation note.
   * Path uses customerId; body carries the note row keys (matches md-vendors DELETE handler + mobile).
   */
  async deleteConsultation(payload: {
    consultation: {
      consultationId: string;
      vendorId: string;
      customerId: string;
      timestamp: string;
    };
  }): Promise<void> {
    try {
      const response = await fetch('/api/consultation/v1/delete', {
        method: 'DELETE',
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete consultation: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting consultation:', error);
      throw error;
    }
  }

  /**
   * Delete consultation note.
   * Path uses customerId; body carries the note row keys (matches md-vendors DELETE handler + mobile).
   */
  async deleteConsultNote(
    customerId: string,
    noteKeys: { id: string; createdDate: number }
  ): Promise<void> {
    try {
      const response = await fetch(`/api/consultation/v1/notes/${customerId}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        body: JSON.stringify({
          consultNote: {
            id: noteKeys.id,
            createdDate: noteKeys.createdDate,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete note: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting consultation note:', error);
      throw error;
    }
  }

  async getOrders(clientId: string, clientType: string, pageSize: number = 10, status?: string, nextLink?: string): Promise<OrdersResponse> {
    try {
      const params = new URLSearchParams();
      params.append('pageSize', String(pageSize));
      params.append('clientId', clientId);
      params.append('clientType', clientType);
      if (status) params.append('status', status);
      if (nextLink) params.append('nextLink', nextLink);

      const response = await fetch(`/api/order/v1/get?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': getToken(),
          'orgId': getOrgId(),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as OrdersResponse;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }
  
  async getOrderItems(bucketId: string): Promise<OrderItemsResponse> {
    try {
      const response = await fetch(`/api/order/v1/items/${bucketId}`, {
        method: 'GET',
        headers: {
          'Authorization': getToken(),
          'orgId': getOrgId(),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch order items: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data as OrderItemsResponse;
    } catch (error) {
      console.error('Error fetching order items:', error);
      throw error;
    }
  }
}

export default new ConsultationService();

